// Client share UI, social intent links, and clipboard integrations (#633).
import { toast } from "./toast.ts";
import { trackSocialAction } from "./telemetry.ts";

export interface SharePayload {
  title: string;
  text: string;
  url: string;
}

/**
 * Generate pre-filled social intent URL for sharing.
 */
export function getShareIntent(platform: "x" | "linkedin" | "reddit", payload: SharePayload): string {
  const enc = encodeURIComponent;
  switch (platform) {
    case "x":
      return `https://x.com/intent/tweet?text=${enc(payload.text)}&url=${enc(payload.url)}`;
    case "linkedin":
      // LinkedIn unfurls metadata directly from og:title/og:image at the URL
      return `https://www.linkedin.com/sharing/share-offsite/?url=${enc(payload.url)}`;
    case "reddit":
      return `https://www.reddit.com/submit?url=${enc(payload.url)}&title=${enc(payload.title)}`;
  }
}

/**
 * Format markdown embed syntax for live SVG badges.
 */
export function getBadgeMarkdown(
  kind: "stable" | "contender",
  slugOrHandle: string,
  base: string = "https://basho.dev",
): string {
  const cleanBase = base.replace(/\/+$/, "");
  if (kind === "stable") {
    return `[![Basho Stable](${cleanBase}/u/${slugOrHandle}/badge.svg)](${cleanBase}/u/${slugOrHandle})`;
  }
  return `[![Basho Contender](${cleanBase}/c/${slugOrHandle}/badge.svg)](${cleanBase}/contender/${slugOrHandle})`;
}

/**
 * Detect touch capability for native Web Share API preference.
 */
export function isTouchDevice(): boolean {
  if (typeof window === "undefined") return false;
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    Boolean(window.matchMedia && window.matchMedia("(pointer: coarse)").matches)
  );
}

/**
 * Copy text to clipboard with legacy fallback and display toast confirmation.
 */
export async function copyWithToast(text: string, message: string, doc: Document = document): Promise<boolean> {
  let ok = false;
  if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch (_) {
      ok = false;
    }
  }

  if (!ok && typeof doc !== "undefined") {
    try {
      const ta = doc.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      doc.body.appendChild(ta);
      ta.focus();
      ta.select();
      ok = doc.execCommand("copy");
      ta.remove();
    } catch (_) {
      ok = false;
    }
  }

  if (ok) {
    toast(`<span>${message}</span>`, "#", doc);
  }
  return ok;
}

/**
 * Initialize share button triggers, intent links, and clipboard actions.
 */
export function initShareUI(doc: Document = document): void {
  doc.addEventListener("click", async (e: MouseEvent) => {
    const target = e.target;
    if (!(target instanceof Element)) return;

    // 1. Share trigger
    const trigger = target.closest("[data-share-trigger]");
    if (trigger instanceof HTMLElement) {
      const title = trigger.dataset.shareTitle || "";
      const text = trigger.dataset.shareText || "";
      const url = trigger.dataset.shareUrl || (typeof window !== "undefined" ? window.location.href : "");

      trackSocialAction("share");

      if (isTouchDevice() && typeof navigator !== "undefined" && typeof navigator.share === "function") {
        e.preventDefault();
        try {
          await navigator.share({ title, text, url });
          return;
        } catch (err: unknown) {
          if (err instanceof Error && err.name === "AbortError") {
            return;
          }
        }
      }

      // If outside a native summary toggle, toggle the parent details popover
      const popover = trigger.closest("details.share-popover");
      if (popover instanceof HTMLDetailsElement && trigger.tagName.toLowerCase() !== "summary") {
        popover.open = !popover.open;
      }
    }

    // 2. Copy link
    const copyLinkBtn = target.closest("[data-copy-link]");
    if (copyLinkBtn instanceof HTMLElement) {
      e.preventDefault();
      const url = copyLinkBtn.dataset.copyLink || (typeof window !== "undefined" ? window.location.href : "");
      await copyWithToast(url, "link copied to clipboard", doc);
      trackSocialAction("share");
    }

    // 2b. Copy an invite's message (/admin/invites)
    const copyMessageBtn = target.closest("[data-copy-message]");
    if (copyMessageBtn instanceof HTMLElement) {
      e.preventDefault();
      await copyWithToast(copyMessageBtn.dataset.copyMessage || "", "message copied", doc);
    }

    // 3. Copy badge markdown
    const copyBadgeBtn = target.closest("[data-copy-badge]");
    if (copyBadgeBtn instanceof HTMLElement) {
      e.preventDefault();
      const md = copyBadgeBtn.dataset.copyBadge || "";
      await copyWithToast(md, "badge markdown copied to clipboard", doc);
      trackSocialAction("share");
    }
  });
}
